import { defineComponent as _defineComponent } from 'vue'
import { computed, getCurrentInstance, nextTick, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import type { PropType } from 'vue';
import { notification } from 'ant-design-vue';
import { ProductRowItem } from '@/interfaces/general/ProductRowItem';
import { UpdateFunctionEntry } from '@/interfaces/components/configurator/UpdateFunctionEntry';
import { ActiveProductFormValueObject } from '@/interfaces/general/ActiveProductFormValueObject';
import { ConfiguratorValue } from '@/interfaces/components/configurator/ConfiguratorValue';
import { determineFieldVisibility, extractUpdateFunctionValues } from '@/helpers/FieldFunctionHelper';
import { UpdateProductFormEntry } from '@/interfaces/components/configurator/UpdateProductFormEntry';
import ChecklistFieldModule from '@/components/views/productView/ChecklistFieldModule.vue';
import { ChecklistField } from '@/interfaces/components/configurator/ChecklistField';
import { debounce } from 'vue-debounce';
import { EventBus } from '@/helpers/EventBusHelper';
import { EventBusEvents } from '@/enums/global/EventBusEvents';
import { PopupEventData } from '@/interfaces/components/PopupEventData';
import { PopupEvents } from '@/enums/global/PopupEvents';
import { MultipositionDeleteMap } from '@/interfaces/general/MultipositionDeleteMap';
import { parseAndTransformStringWithDynamicValues } from '@/helpers/JsonLogicHelper';
import { areTwoProductFormValuesSame } from '@/helpers/FormDataHelper';
import { checklistFieldsWithErrors } from '@/helpers/OfferHelper';
import Offer from '@/models/Offer';
import { filterProductsFromOfferItems as filterOfferItems } from '@/helpers/OfferItemTableHelper';
import OfferItem from '@/models/OfferItem';
import { TransformedTableOfferItem } from '@/interfaces/components/ProductsInOffer/TransformedTableOfferItem';
import { defaultValueWarnings as defaultValueWarningsConfig } from '@/interfaces/components/projectNew/DefaultValueWarnings';
import i18n from '@/i18n';
import store from '@/store';
import { ChecklistFieldEntry } from '@/interfaces/components/configurator/ChecklistFieldEntry';

/* === Props & Emits === */

export default /*#__PURE__*/_defineComponent({
  __name: 'ProductSlotRow',
  props: {
    addNewRow: {
        type: Function as PropType<
            (numberOfRows: number, existingProductValues?: ChecklistFieldEntry[]) => void | Promise<void>
        >,
        default: null,
    },
    updateItemRowsIsUpdatingState: {
        type: Function as PropType<(itemRowIndex: number, newState: boolean) => void>,
        default: null,
    },
    itemRowIndex: { type: Number, required: true },
    productFormId: { type: String, required: true },
    isFirst: { type: Boolean, required: true },
    isUpdating: { type: Boolean, required: true },
    isLoading: { type: Boolean, default: false },
    hiddenIndices: { type: Array as PropType<boolean[]>, required: true },
    offer: { type: Object as PropType<Offer | null>, default: null },
    isCustomProduct: { type: Boolean, default: false },
    isEditMode: { type: Boolean, required: true },
    productData: { type: Object as PropType<ProductRowItem>, required: true },
    deleteExistingRow: {
        type: Function as PropType<(multipositionDeleteMap: MultipositionDeleteMap) => void>,
        required: true,
    },
    level: { type: Number, required: true },
    isAddingProducts: { type: Boolean, default: false },
    shouldResetReadOnlyFields: { type: Boolean, default: false },
    productDetails: { required: false, default: null },
    checklistSegmentName: { type: String, required: false },
},
  emits: ['is-production', 'updateHiddenIndices', 'deleteRow'],
  setup(__props, { expose, emit }) {

const props = __props;




const { proxy } = getCurrentInstance()!;
const componentProxy = proxy as any;

/* === State & Store Access === */
const wrapper = ref<HTMLElement | null>(null);
const hasPriceListError = ref(false);
const isDisabledFieldArray = ref<boolean[]>([]);
const priceListError = ref('');
const isActive = ref(false);
const pIdFieldErrors = ref<string[]>([]);
const temporaryDisabledPIDs = ref<string[]>([]);
let oldProductFormValues: ActiveProductFormValueObject | null = null;
let watchFunctionInstance: null | (() => void) = null;

expose({ wrapper, isDisabledFieldArray });

const updateActiveProductFormValue = (entry: UpdateProductFormEntry) =>
    store.dispatch('configurator/updateActiveProductFormValue', entry);
const getProductFormValues = (productFormId: number): ActiveProductFormValueObject | null =>
    store.getters['configurator/activeProductForm'](productFormId);

const debouncedUpdateDisabledStates: any = debounce(() => {
    updateDisabledStates();
}, 200);
const debouncedrunUpdateFunctions: any = debounce(
    (
        updateFunctions: UpdateFunctionEntry[],
        activeProductFormId: number,
        productFormValues: ActiveProductFormValueObject,
    ) => {
        runUpdateFunctions(updateFunctions, activeProductFormId, productFormValues);
    },
    100,
);

const debouncedUpdateItemRowsIsUpdatingState = debounce((itemRowIndex: number, newState: boolean) => {
    props.updateItemRowsIsUpdatingState!(itemRowIndex, newState);
}, 150);
const debouncedRunOptionsFunctions = debounce(() => {
    runOptionsFunctions();
}, 100);

const debounceToggleIsActive = debounce((state) => {
    toggleIsActive(state);
}, 250);

/* === Derived Row State === */
const shouldShowErrorClass = computed(() => checklistFieldsWithErrors(props.productData).length > 0);

const shouldShowWarningClass = computed(
    () => !shouldShowErrorClass.value && checklistFieldsWithErrors(props.productData, true).length > 0,
);

const productFormValues = computed(() => getProductFormValues(props.productData.activeProductFormId));

const showConnectionColor = computed(
    () =>
        props.productData.connection !== null &&
        !shouldShowWarningClass.value &&
        !shouldShowErrorClass.value &&
        !isActive.value,
);

const defaultValueWarnings = computed(() => {
    const warnings = store.getters['configurator/defaultValueWarnings'];

    if (warnings.length) {
        return warnings.find(
            (warning: defaultValueWarningsConfig) => warning.productFormId === props.productData.activeProductFormId,
        );
    }

    return null;
});

const productCombinationStyle = computed(() => {
    if (showConnectionColor.value) {
        return { 'background-color': props.productData.color };
    }

    return '';
});

function toggleIsActive(state: boolean) {
    isActive.value = state;
}

function filterProductsFromOfferItems(
    offerItems: Array<OfferItem | TransformedTableOfferItem>,
): Array<OfferItem | TransformedTableOfferItem> {
    return filterOfferItems(offerItems, false) as Array<OfferItem | TransformedTableOfferItem>;
}

/* === Update Functions === */
async function runUpdateFunctions(
    updateFunctions: UpdateFunctionEntry[],
    activeProductFormId: number,
    currentProductFormValues: ActiveProductFormValueObject,
) {
    updateFunctions.forEach((entry: UpdateFunctionEntry) => {
        if (activeProductFormId == null) {
            return Promise.reject();
        }

        const updatedValues: ConfiguratorValue[] = extractUpdateFunctionValues(
            entry.updateFunctions,
            entry.type,
            currentProductFormValues,
        );

        if (updatedValues.length > 0) {
            if (!checkIfSelectedValueIsValid(entry.id, updatedValues[0])) {
                return;
            }

            updateActiveProductFormValue({
                pId: entry.id,
                value: updatedValues[0],
                productFormId: activeProductFormId,
            });
        }
    });

    return Promise.resolve();
}

function checkIfSelectedValueIsValid(id: string, value: ConfiguratorValue) {
    // We return false only if selected value was not in dropdown options at start and is missing right now
    if (!defaultValueWarnings.value) {
        return true;
    }

    const field = props.productData.editableFields.find((field) => field.pId === id);

    if (!field) {
        return true;
    }

    if (field.fieldType !== 'dropdown') {
        return true;
    }

    const isValueAvailable = field.checklistDropdownField?.checklistDropdownOptions.some((dropdownOption: any) => {
        return dropdownOption.vId === value;
    });

    if (isValueAvailable) {
        return true;
    }

    const invalidValueWasDetectedAtMount = defaultValueWarnings.value.warnings.some(
        (warning: any) => warning.pId === id,
    );

    return !invalidValueWasDetectedAtMount;
}

/* === Color & Visibility Updates === */
function updateColorValue(payload: {
    activeProductFormId: number;
    colorSystemPID: string;
    colorCollection: string;
    colorDropdownPID: string;
    selectedColorValue: string | null;
    shouldFreezeManualInput?: boolean;
}) {
    if (payload.activeProductFormId !== props.productData.activeProductFormId) {
        return;
    }
    updateActiveProductFormValue({
        pId: payload.colorSystemPID,
        value: payload.colorCollection,
        productFormId: payload.activeProductFormId,
    });

    if (payload.selectedColorValue) {
        nextTick(() => {
            updateActiveProductFormValue({
                pId: payload.colorDropdownPID,
                value: payload.selectedColorValue as string,
                productFormId: payload.activeProductFormId,
            });
        });

        // In case we have a selected color value, we should freeze manual input
        if (payload.shouldFreezeManualInput && !temporaryDisabledPIDs.value.includes(payload.colorDropdownPID)) {
            temporaryDisabledPIDs.value.push(payload.colorDropdownPID);
        }

        if (!payload.shouldFreezeManualInput) {
            const index = temporaryDisabledPIDs.value.indexOf(payload.colorDropdownPID);
            if (index > -1) {
                temporaryDisabledPIDs.value.splice(index, 1);
            }
        }
    }
}

async function updateDisabledStates() {
    const currentProductFormValues = getProductFormValues(props.productData.activeProductFormId);
    if (currentProductFormValues == null) {
        return;
    }

    isDisabledFieldArray.value = props.productData.editableFields.map((editableField: ChecklistField) => {
        if (editableField.isSegmentLocked) {
            return true;
        }

        if (!checklistFieldHasVisibilityFunction(editableField)) {
            return false;
        }

        return !determineFieldVisibility(editableField.checklistVisibleFunction, currentProductFormValues);
    });
    emit('updateHiddenIndices');
    onChecklistError();
}

function checklistFieldHasVisibilityFunction(checklistField: ChecklistField) {
    return checklistField.checklistVisibleFunction != null;
}

function onUpdatePriceListError(newValue: boolean) {
    hasPriceListError.value = newValue;
}

function onOpenContactPopup() {
    const popupData: PopupEventData = {
        popupEvent: PopupEvents.contact,
        data: {
            source: 'product-slot-row',
        },
    };

    EventBus.$emit(EventBusEvents.openContactPopup, popupData);
}

function onDelete() {
    emit('deleteRow');
}

function updateDisabledFieldsInMultiposition(payload: {
    activeProductFormId: number;
    colorSystemPID: string;
    fieldValue: string;
    colorDropdownPID: string;
    selectedColorValue: string | null;
    shouldFreezeManualInput?: boolean;
}) {
    if (payload.shouldFreezeManualInput && !temporaryDisabledPIDs.value.includes(payload.colorDropdownPID)) {
        temporaryDisabledPIDs.value.push(payload.colorDropdownPID);
    }

    if (!payload.shouldFreezeManualInput) {
        const index = temporaryDisabledPIDs.value.indexOf(payload.colorDropdownPID);
        if (index > -1) {
            temporaryDisabledPIDs.value.splice(index, 1);
        }
    }
}

/* === Options & Reactive Form Updates === */
function runOptionsFunctions() {
    if (productFormValues.value == null) {
        return;
    }
    console.time('Execution Time');
    if (componentProxy.$refs.dropdownModules) {
        (componentProxy.$refs.dropdownModules as any[]).forEach((dropdownModule) => {
            (dropdownModule.$refs.field as any).runVisibleOptionVIDs({
                activeProductFormValues: productFormValues.value as ActiveProductFormValueObject,
                activeProductFormId: props.productData.activeProductFormId,
            });
        });
    }

    // @ts-ignore
    debouncedUpdateItemRowsIsUpdatingState(props.productData.activeProductFormId, false);
    console.timeEnd('Execution Time');
}

async function onProductFormValueChange(
    currentProductFormValues: ActiveProductFormValueObject = productFormValues.value as any,
) {
    if (oldProductFormValues && areTwoProductFormValuesSame(currentProductFormValues, oldProductFormValues)) {
        return;
    }

    const productFormValuesCopy = Object.assign({}, currentProductFormValues);
    oldProductFormValues = Object.freeze(productFormValuesCopy);

    props.updateItemRowsIsUpdatingState!(props.productData.activeProductFormId, true);
    nextTick(async () => {
        debouncedrunUpdateFunctions(
            props.productData.updateFunctions,
            props.productData.activeProductFormId,
            currentProductFormValues,
        );
        debouncedUpdateDisabledStates();
        debouncedRunOptionsFunctions();
    });
}

/* === Checklist Notifications === */
function onChecklistError() {
    let numberOfNotifications = document.getElementsByClassName(
        `active-product-form-notification-${props.productData.activeProductFormId}`,
    ).length;
    while (numberOfNotifications > 0) {
        notification.close(`${props.productData.activeProductFormId}${numberOfNotifications - 1}`);
        numberOfNotifications--;
    }
    EventBus.$emit(EventBusEvents.clearNotifications, props.itemRowIndex);
    let index = 0;

    const checklistErrors = checklistFieldsWithErrors(props.productData, true);
    for (const checklistField of checklistErrors) {
        window.setTimeout(() => {
            if (checklistField.checklistStringField) {
                // && checklistField.checklistStringField.defaultValue
                switch (checklistField.fieldType) {
                    case 'abort':
                        EventBus.$emit(EventBusEvents.showNotification, {
                            index: props.itemRowIndex,
                            title: i18n.t('Dogodila se greška') as string,
                            description: parseAndTransformStringWithDynamicValues(
                                checklistField.checklistStringField.defaultValue ?? '',
                                checklistField.checklistStringField.functions,
                                productFormValues.value,
                                'function',
                            ),
                            type: 'error',
                        });
                        break;
                    case 'warning':
                        EventBus.$emit(EventBusEvents.showNotification, {
                            index: props.itemRowIndex,
                            title: i18n.t('Upozorenje') as string,
                            description: parseAndTransformStringWithDynamicValues(
                                checklistField.checklistStringField.defaultValue ?? '',
                                checklistField.checklistStringField.functions,
                                productFormValues.value,
                                'function',
                            ),
                            type: 'warning',
                        });
                        break;
                }
                index++;
            }
        }, 50);
    }
}

/* === Lifecycle === */
onMounted(() => {
    watchFunctionInstance = watch(productFormValues, onProductFormValueChange, {
        deep: true,
        immediate: true,
    });
    // @ts-ignore
    debouncedUpdateItemRowsIsUpdatingState(props.productData.activeProductFormId, false);
    EventBus.$on(EventBusEvents.updateSystemColorValue, updateColorValue);
    EventBus.$on(EventBusEvents.updateDisabledFieldsInMultiposition, updateDisabledFieldsInMultiposition);
});

onBeforeUnmount(() => {
    watchFunctionInstance?.();
    EventBus.$off(EventBusEvents.updateSystemColorValue, updateColorValue);
    EventBus.$off(EventBusEvents.updateDisabledFieldsInMultiposition, updateDisabledFieldsInMultiposition);
});

return { __sfc: true,props, emit, proxy, componentProxy, wrapper, hasPriceListError, isDisabledFieldArray, priceListError, isActive, pIdFieldErrors, temporaryDisabledPIDs, oldProductFormValues, watchFunctionInstance, updateActiveProductFormValue, getProductFormValues, debouncedUpdateDisabledStates, debouncedrunUpdateFunctions, debouncedUpdateItemRowsIsUpdatingState, debouncedRunOptionsFunctions, debounceToggleIsActive, shouldShowErrorClass, shouldShowWarningClass, productFormValues, showConnectionColor, defaultValueWarnings, productCombinationStyle, toggleIsActive, filterProductsFromOfferItems, runUpdateFunctions, checkIfSelectedValueIsValid, updateColorValue, updateDisabledStates, checklistFieldHasVisibilityFunction, onUpdatePriceListError, onOpenContactPopup, onDelete, updateDisabledFieldsInMultiposition, runOptionsFunctions, onProductFormValueChange, onChecklistError, ChecklistFieldModule }
}

})